export const MAPTILER_KEY = 'CIMCMzWVRP7YnkotZKM3';
export const MAPTILER_MAP_ID = '019f0317-b046-74ed-9e5c-200a9e03bd7a';
export const MAPTILER_TILE_URL = `https://api.maptiler.com/maps/${MAPTILER_MAP_ID}/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`;

export const ADMIN_BOUNDARIES_WMS = 'https://data.apps.fao.org/map/gsrv/gsrv1/boundaries/wms';
export const PLACE_LABELS_WMS = 'https://data.apps.fao.org/map/gsrv/gsrv1/boundaries/wms/v2';

export type MapCrsKey = 'EPSG3857' | 'EPSG4326';

/**
 * Default display projection per background option — this is the MapContainer's `crs` used when a
 * background is first selected, independent of whatever SRS the underlying request happens to use.
 *
 * - OpenStreetMap, MapTiler, Satellite: standard XYZ tiles baked as a Web Mercator (EPSG3857)
 *   pyramid. Only EPSG3857 works — see BACKGROUND_LAYER_SUPPORTED_CRS.
 * - Crop Suitability: real pre-cut WMTS tiles (see SoilFERWMTS in MapView.tsx). The FAO layer
 *   publishes both an EPSG:4326 (GoogleCRS84Quad) and an EPSG:3857 (GoogleMapsCompatible) tile
 *   matrix set — confirmed via GetCapabilities — so the request's tilematrixset param switches
 *   with the user's choice, matched by the MapContainer's crs. Web Mercator is locally conformal
 *   (looks "perpendicular" when zoomed into a field regardless of latitude); plate carrée
 *   (EPSG4326) stretches shapes E-W away from the equator by a factor of 1/cos(lat).
 * - Crop Statistics: NOT tiled — WmsImageLayer requests one WMS image sized to the current
 *   viewport on every pan/zoom (the only SRS the gaul2_stats layer supports server-side is
 *   EPSG:4326, so that request stays fixed regardless of projection choice). Because it's a
 *   single image stretched across the current bounds rather than a tile pyramid, it can safely be
 *   *displayed* on either projection: Web Mercator leaves only a negligible residual warp (the
 *   variation in Mercator's scale factor within one screen's worth of latitude); EPSG4326 shows
 *   the image exactly as the server rendered it (true plate carrée, distorted away from the
 *   equator but pixel-faithful to the source).
 */
export const BACKGROUND_LAYER_CRS: Record<string, MapCrsKey> = {
  'OpenStreetMap': 'EPSG3857',
  'MapTiler': 'EPSG3857',
  'Satellite': 'EPSG3857',
  'Crop Suitability': 'EPSG3857',
  'Crop Statistics': 'EPSG3857',
};

/**
 * Which projections each background can actually render correctly in. OpenStreetMap, MapTiler and
 * Satellite are locked to Web Mercator (no other tile source exists for them); Crop Suitability and
 * Crop Statistics genuinely support both, per the reasoning in BACKGROUND_LAYER_CRS above.
 */
export const BACKGROUND_LAYER_SUPPORTED_CRS: Record<string, MapCrsKey[]> = {
  'OpenStreetMap': ['EPSG3857'],
  'MapTiler': ['EPSG3857'],
  'Satellite': ['EPSG3857'],
  'Crop Suitability': ['EPSG3857', 'EPSG4326'],
  'Crop Statistics': ['EPSG3857', 'EPSG4326'],
};

/**
 * Initial map view on load, before a location is picked. Centred on Africa; at zoom 3 the continent
 * (~37°N to ~35°S, ~18°W to ~52°E) is ~440px tall by ~400px wide in Web Mercator, so it fits whole on
 * typical desktop and tablet viewports. Leaflet's default zoomSnap is 1, so use integer zooms here.
 */
export const DEFAULT_MAP_CENTER: [number, number] = [2, 17];
export const DEFAULT_MAP_ZOOM = 3;
